import { HttpClient } from '@angular/common/http'
import { Injectable } from '@angular/core'
import { Observable } from 'rxjs'
import { environment } from '../../../environments/environment'
import {
  Employees,
  EstabCamera,
  Estabelecimento,
  EstabUser,
  Estado,
  FormaPagamento,
  Planos,
  RouterInfo,
  ShopsEmployees,
  TipoEstabelecimento
} from './estabelecimento.model'
import { AllUserEstabs } from '../users/users.model'

@Injectable({
  providedIn: 'root'
})
export class PvtEstabelecimentoService {
  constructor(private http: HttpClient) {}

  getAllEstabelecimentos(): Observable<AllUserEstabs[]> {
    return this.http.get<any>(`${environment.apiURL}/estabelecimento`, { withCredentials: true })
  }

  getEstabelecimento(id: string): Observable<[Estabelecimento]> {
    return this.http.get<any>(`${environment.apiURL}/estabelecimento/${id}`, {
      withCredentials: true
    })
  }

  /**
   *
   * Sobre o local físico:
   * @param nome
   * @param id campo auto-gerado com base no nome
   * @param cep composto por 8 caracteres (string), sendo apenas números (sem traço)
   * @param numero no formato string, caso sem número deverá ser: 'S/N'
   * @param endereço
   * @param cidade
   * @param estado apenas as siglas de 2 caracteres
   * Sobre o plano:
   * @param tipo de estabelecimento. Ex: 'subway', 'posto br' ou 'ops';
   * @param pagamento
   * @param planos ex: checkout tem planos de 0, 4, 7 e 31 créditos (dias)
   * Sobre o erp:
   * @param erp_empresa ex: 'sem erp' ou 'swfast' ou 'linx'
   *  Os três campos seguintes são obrigatórios no swfast e opcionais no linx
   * @param loja
   * @param user_erp
   * @param pwd_erp
   * Sobre o roteador:
   * @param vpn_user
   * @param ip_address O formato padrão é a string '10.8.0.X', onde X é outro número
   * @returns
   */

  cadastrarEstabelecimento(
    nome: string,
    id: string,
    cnpj: string,
    cep: string,
    numero: string,
    endereço: string,
    cidade: string,
    estado: Estado,
    pais: string,
    currency: string,
    timezone: string,
    tipo: TipoEstabelecimento,
    pagamento: FormaPagamento,
    planos: Planos,
    userInCharge: string,
    erp_empresa: string,
    vpn_user: string,
    ip_address: string,
    usuarios: EstabUser[],
    cameras: EstabCamera[],
    scheduling_requirements: string,
    contract: string,
    extraServices: string[],
    funcionarios: { nome: string }[] = []
  ): Observable<any> {
    const estabelecimento: Estabelecimento = this.setEstabelecimento(
      nome,
      id,
      cnpj,
      cep,
      numero,
      endereço,
      cidade,
      estado,
      pais,
      currency,
      timezone,
      tipo,
      pagamento,
      planos,
      userInCharge,
      erp_empresa,
      vpn_user,
      ip_address,
      usuarios,
      cameras,
      scheduling_requirements,
      contract,
      extraServices,
      funcionarios
    )

    if (estabelecimento.cnpj === '') delete estabelecimento.cnpj

    return this.http.post<any>(`${environment.apiURL}/estabelecimento`, estabelecimento, {
      withCredentials: true,
      responseType: 'text' as 'json'
    })
  }

  updateEstabelecimento(
    nome: string,
    id: string,
    cnpj: string,
    cep: string,
    numero: string,
    endereço: string,
    cidade: string,
    estado: Estado,
    pais: string,
    currency: string,
    timezone: string,
    tipo: TipoEstabelecimento,
    pagamento: FormaPagamento,
    planos: Planos,
    userInCharge: string,
    erp_empresa: string,
    vpn_user: string,
    ip_address: string,
    usuarios: EstabUser[],
    cameras: EstabCamera[],
    scheduling_requirements: string,
    contract: string,
    extraServices: string[],
    funcionarios: { nome: string }[]
  ): Observable<any> {
    const estabelecimento: Estabelecimento = this.setEstabelecimento(
      nome,
      id,
      cnpj,
      cep,
      numero,
      endereço,
      cidade,
      estado,
      pais,
      currency,
      timezone,
      tipo,
      pagamento,
      planos,
      userInCharge,
      erp_empresa,
      vpn_user,
      ip_address,
      usuarios,
      cameras,
      scheduling_requirements,
      contract,
      extraServices,
      funcionarios
    )

    if (estabelecimento.cnpj === '') delete estabelecimento.cnpj

    return this.http.put<any>(
      `${environment.apiURL}/estabelecimento/${estabelecimento._id}`,
      estabelecimento,
      { withCredentials: true, responseType: 'text' as 'json' }
    )
  }

  /**
   * Apenas monta o objeto estabelecimento
   */
  setEstabelecimento(
    nome: string,
    id: string,
    cnpj: string,
    cep: string,
    numero: string,
    endereço: string,
    cidade: string,
    estado: Estado,
    pais: string,
    currency: string,
    timezone: string,

    tipo: TipoEstabelecimento,
    pagamento: FormaPagamento,
    planos: Planos,
    userInCharge: string,
    erp_empresa: string,
    vpn_user: string,
    ip_address: string,
    usuarios: EstabUser[],
    cameras: EstabCamera[],
    scheduling_requirements: string,
    contract: string,
    extraServices: string[],
    funcionarios: { nome: string }[]
  ): Estabelecimento {
    return {
      nome: nome,
      _id: id,
      cnpj: cnpj,
      cep: cep,
      numero: numero,
      endereço: endereço,
      cidade: cidade,
      estado: estado,
      pais: pais,
      currency: currency,
      timezone: timezone,

      scheduling_requirements: scheduling_requirements,
      tipo: tipo,
      pagamento: pagamento,
      userInCharge: userInCharge,
      erp: {
        empresa: erp_empresa
      },
      planos: planos,

      vpn_user: vpn_user,
      ip_address: ip_address,
      usuarios: usuarios,
      cameras: cameras,
      contract: contract,
      extraServices: extraServices,
      funcionarios: funcionarios
    }
  }

  deleteEstabelecimento(id: string): Observable<any> {
    return this.http.delete<any>(`${environment.apiURL}/estabelecimento/${id}`, {
      withCredentials: true
    })
  }

  /**
   * Atualiza os usuários de um estabelecimento
   * @param estabelecimentoID
   * @param payload objeto contendo campo usuarios com a lista de usuários a serem eviados para o backend
   * { usuarios: [{user: "user.exemplo", tipo: "normal"}] }
   * @returns
   */
  updateEstabelecimentoUsers(
    estabelecimentoID: string,
    payload: { usuarios: EstabUser[] }
  ): Observable<any> {
    return this.http.put(
      `${environment.apiURL}/estabelecimento/addusertoestabelecimento/${estabelecimentoID}`,
      payload,
      { withCredentials: true, responseType: 'text' }
    )
  }

  /**
   * Remove usuário do estabelecimento
   * @param estabelecimentoID
   * @param payload objeto contendo campo usuarios com a lista de usuários a serem removidos do estabelecimento
   * { usuarios: [{user: "user.exemplo", tipo: "normal"}] }
   * @returns
   */
  removeUserFromEstabelecimento(
    estabelecimentoID: string,
    payload: { usuarios: EstabUser[] }
  ): Observable<any> {
    return this.http.put(
      `${environment.apiURL}/estabelecimento/removeuserfromestabelecimento/${estabelecimentoID}`,
      payload,
      { withCredentials: true, responseType: 'text' as 'json' }
    )
  }

  /**
   * Atualiza as câmeras de um estabelecimento
   * @param estabelecimentoID
   * @param payload objeto contendo campo cameras com a lista de usuários a serem eviados para o backend
   * @returns
   */
  updateEstabelecimentoCameras(
    estabelecimentoID: string,
    payload: { cameras: EstabCamera[] }
  ): Observable<any> {
    return this.http.put(
      `${environment.apiURL}/estabelecimento/addcamerastoestabelecimento/${estabelecimentoID}`,
      payload,
      { withCredentials: true, responseType: 'text' }
    )
  }

  /**
   * Remove câmeras do estabelecimento
   * @param estabelecimentoID
   * @param payload objeto contendo campo cameras com a lista de câmeras a serem removidas do estabelecimento
   * @returns
   */
  removeCamerasFromEstabelecimento(
    estabelecimentoID: string,
    payload: { cameras: string[] }
  ): Observable<any> {
    return this.http.put(
      `${environment.apiURL}/estabelecimento/removecamerasfromestabelecimento/${estabelecimentoID}`,
      payload,
      { withCredentials: true, responseType: 'text' as 'json' }
    )
  }

  updateEstabelecimentoRouter(estabelecimentoID: string, payload: RouterInfo) {
    return this.http.put(`${environment.apiURL}/estabelecimento/${estabelecimentoID}`, payload, {
      withCredentials: true
    })
  }

  getEmployeesFromAllShops(): Observable<ShopsEmployees[]> {
    return this.http.get<ShopsEmployees[]>(`${environment.apiURL}/estabelecimento/funcionarios`, {
      withCredentials: true
    })
  }

  getEmployeesByShop(shopId: string): Observable<Employees> {
    return this.http.get<Employees>(
      `${environment.apiURL}/estabelecimento/funcionarios/${shopId}`,
      { withCredentials: true }
    )
  }

  addEmployeeToShop(shopId: string, employee: { nome: string }): Observable<any> {
    return this.http.post(
      `${environment.apiURL}/estabelecimento/funcionarios/${shopId}`,
      { funcionarios: [employee] },
      { withCredentials: true, responseType: 'text' as 'json' }
    )
  }

  removeEmployeeFromShop(shopId: string, employee: { nome: string }): Observable<any> {
    return this.http.delete(`${environment.apiURL}/estabelecimento/funcionarios/${shopId}`, {
      body: { funcionarios: [employee] }
    })
  }

  addEmployeesToShop(shopId: string, employees: { nome: string }[]): Observable<any> {
    return this.http.post(
      `${environment.apiURL}/estabelecimento/funcionarios/${shopId}`,
      { funcionarios: employees },
      { withCredentials: true, responseType: 'text' as 'json' }
    )
  }

  removeEmployeesFromShop(shopId: string, employees: { nome: string }[]): Observable<any> {
    return this.http.delete(`${environment.apiURL}/estabelecimento/funcionarios/${shopId}`, {
      body: { funcionarios: employees }
    })
  }

  updateEstabelecimentoERP(shopId: string, payload: { erp: { empresa: string }; cnpj?: string }) {
    if (!payload.cnpj) {
      delete payload.cnpj
    }

    return this.http.put(`${environment.apiURL}/estabelecimento/${shopId}`, payload, {
      withCredentials: true
    })
  }

  updateSchedulingRequirements(shopId: string, scheduling_requirements: string) {
    return this.http.put(`${environment.apiURL}/estabelecimento/${shopId}`, {
      scheduling_requirements
    }, {
      withCredentials: true
    })
  }
}
